export const calculations = [
  {
    id: 1,
    label: "Subtotal Part Wt",
    subtitle: "Parts Per Tree x Weight",
    fields: [{ mult: ["partsPerTree", "weight"] }],
    type: "lbs",
  },
  {
    id: 2,
    label: "Tree Weight",
    fields: ["tree.weight"],
    type: "lbs",
  },
  {
    id: 3,
    label: "PPT Weight + Tree Weight",
    fields: [{ sum: [2, 1] }],
    type: "lbs",
  },
  {
    id: 4,
    label: "Tree Metal Loss Factor",
    fields: ["treeMetalLossFactor"], // Rate
    highlight: "yellowMainTransparent",
    type: "percentage",
  },
  {
    id: 5,
    label: "Remelt",
    fields: ["material.remelt"],
    type: "boolean",
  },
  {
    id: 6,
    label: "Metal Loss lbs",
    subtitle: "No Remelt 0, else Tree Loss Factor x PPT & Tree lbs",
    fields: [{ if: { eq: [5, false] }, then: "0", else: { mult: [4, 3] } }],
  },
  {
    id: 7,
    label: "Metal Usage",
    subtitle:
      "Sub Part lbs + Metal Loss + Tree lbs (If No Remelt 0)",
    fields: [{ sum: [1, 6, { if: { eq: [5, false] }, then: 2, else: "0" }] }],
  },
  {
    id: 8,
    label: "Metal Price/lbs",
    fields: ["material.price"], // Location - Material Cost
    type: "currency",
    highlight: "yellowMainTransparent",
  },
  {
    id: 9,
    label: "Metal Cost",
    subtitle: "Metal Usage * Metal Price",
    type: "currency",
    fields: [{ mult: [7, 8] }],
  },
  {
    id: 10,
    label: "Metal Cost / PPT",
    type: "currency",
    fields: [{ div: [9, "partsPerTree"] }],
  },
  {
    id: 11,
    label: "Wax Part Price/lb",
    type: "currency",
    fields: ["waxPartCost"], // Location - Wax Part Cost
    highlight: "yellowMainTransparent",
  },
  {
    id: 12,
    label: "Cost Part Wax",
    subtitle: "Weight * Wax Part Cost / 7.5",
    type: "currency",
    fields: [{ div: [{ mult: ["weight", 11] }, "7.5"] }],
  },
  {
    id: 13,
    label: "Wax Tree Price/lb", // Location - Wax Tree Cost
    type: "currency",
    fields: ["waxTreeCost"],
    highlight: "yellowMainTransparent",
  },
  {
    id: 14,
    label: "Cost Tree Wax/Part",
    subtitle: "Tree Weight * Wax Tree Cost / 7.5 / PPT",
    type: "currency",
    fields: [{ div: [{ div: [{ mult: [2, 13] }, "7.5"] }, "partsPerTree"] }],
  },
  {
    id: 15,
    label: "Subtotal Wax Cost",
    subtitle: "Cost Tree Wax/Part + Cost Part Wax",
    type: "currency",
    fields: [{ sum: [12, 14] }],
  },
  {
    id: 16,
    label: "Tree Adjustments",
    subtitle: "Max of PPT Tree Weight / 50 or 1",
    fields: [{ max: [{ div: [3, "50"] }, "1"] }],
  },
  {
    id: 17,
    label: "Utilities Historical Cost * Tree Adjustment",
    type: "currency",
    fields: [{ mult: ["historical.treeCost", 16] }],
    highlight: "redMainTransparent",
  },
  {
    id: 18,
    label: "Utilities Cost",
    subtitle: "Historical Tree Cost / Parts Per Tree",
    type: "currency",
    fields: [{ div: [17, "partsPerTree"] }],
  },
  {
    id: 19,
    type: "currency",
    label: "Materials Historical Cost * Tree Adjustment",
    fields: [{ mult: ["historical.materialCost", 16] }],
    highlight: "redMainTransparent",
  },
  {
    id: 20,
    type: "currency",
    label: "Other Cost",
    subtitle: "Historical Material Cost * Tree Adjustment",
    fields: [{ div: [19, "partsPerTree"] }],
  },
  {
    id: 21,
    type: "currency",
    label: "Total Materials",
    subtitle: "Historical Material & Tree Cost + Subtotal Wax & Metal Cost",
    fields: [{ sum: [20, 18, 15, 10] }],
  },
  {
    id: 22,
    label: "Factor/Tree",
    type: "currency",
    fields: [{ mult: ["historical.factor", 16] }],
    highlight: "redMainTransparent",
  },
  {
    id: 23,
    label: "Labor (Factor/Tree) / PPT",
    type: "currency",
    fields: [{ div: [22, "partsPerTree"] }],
  },
  {
    id: 24,
    label: "Materials + Labor",
    type: "currency",
    fields: [{ sum: [23, 21] }],
  },
  {
    id: 25,
    label: "COGS",
    subtitle: "(Materials + Labor) / (1 - Scrap Rate)",
    type: "currency",
    fields: [{ div: [24, { sub: ["1", { div: ["scrapRate", "100"] }] }] }],
  },
  {
    id: 26,
    label: "Rejection Cost",
    subtitle: "COGS * Scrap Rate",
    type: "currency",
    fields: [{ mult: [25, { div: ["scrapRate", "100"] }] }],
  },
];

// Resolve a dot-path like "material.price" from data
const getField = (data, path) => {
  const parts = path.split(".");
  let value = data;
  for (const part of parts) {
    value = value?.[part];
    if (value == null) return null;
  }
  return value != null ? Number(value) : null;
};

// Resolve a single operand — can be a calc id (int), field path (string), literal ("123"), or nested expression (object)
const resolveOperand = (operand, data, cache) => {
  if (operand == null) return null;

  // Integer = reference to another calculation's result
  if (typeof operand === "number" && Number.isInteger(operand)) {
    return cache[operand] ?? null;
  }

  // Boolean literal
  if (typeof operand === "boolean") return operand;

  // String — either a literal number in quotes or a field path
  if (typeof operand === "string") {
    const num = Number(operand);
    if (!isNaN(num) && operand.trim() !== "") return num;
    return getField(data, operand);
  }

  // Object = nested expression
  if (typeof operand === "object") {
    return resolveExpression(operand, data, cache);
  }

  return null;
};

// Resolve an expression object like { mult: [...] }, { sum: [...] }, { if: ..., then: ..., else: ... }
const resolveExpression = (expr, data, cache) => {
  if (expr == null) return null;

  // Conditional: { if: { eq: [a, b] }, then: ..., else: ... }
  if ("if" in expr) {
    const condition = expr.if;
    const [condKey] = Object.keys(condition);
    const [a, b] = condition[condKey];
    const valA = resolveOperand(a, data, cache);
    const valB = resolveOperand(b, data, cache);

    let result = false;
    if (condKey === "eq") result = valA === valB;
    else if (condKey === "neq") result = valA !== valB;
    else if (condKey === "gt") result = valA > valB;
    else if (condKey === "lt") result = valA < valB;

    return resolveOperand(result ? expr.then : expr.else, data, cache);
  }

  // Math operations
  if ("mult" in expr) {
    const values = expr.mult.map((o) => resolveOperand(o, data, cache));
    if (values.some((v) => v == null)) return null;
    return values.reduce((acc, v) => acc * v, 1);
  }

  if ("sum" in expr) {
    const values = expr.sum.map((o) => resolveOperand(o, data, cache));
    if (values.some((v) => v == null)) return null;
    return values.reduce((acc, v) => acc + v, 0);
  }

  if ("sub" in expr) {
    const values = expr.sub.map((o) => resolveOperand(o, data, cache));
    if (values.some((v) => v == null)) return null;
    return values.reduce((acc, v, i) => (i === 0 ? v : acc - v));
  }

  if ("div" in expr) {
    const [num, den] = expr.div.map((o) => resolveOperand(o, data, cache));
    if (num == null || den == null || den === 0) return null;
    return num / den;
  }

  if ("max" in expr) {
    const values = expr.max.map((o) => resolveOperand(o, data, cache));
    if (values.some((v) => v == null)) return null;
    return Math.max(...values);
  }

  if ("min" in expr) {
    const values = expr.min.map((o) => resolveOperand(o, data, cache));
    if (values.some((v) => v == null)) return null;
    return Math.min(...values);
  }

  return null;
};

// Resolve a calculation entry's fields array
const resolveFields = (fields, data, cache) => {
  if (!fields?.length) return null;

  const field = fields[0];

  // Direct field reference: ["material.price"]
  if (typeof field === "string") {
    return getField(data, field);
  }

  // Expression object
  if (typeof field === "object") {
    return resolveExpression(field, data, cache);
  }

  return null;
};

export const resolveCalculations = (data = {}) => {
  const cache = {};

  for (const calc of calculations) {
    cache[calc.id] = resolveFields(calc.fields, data, cache);
  }

  return cache;
};

export const getQuoteFormValues = (watch, entities = {}, extras = {}) => {
  const {
    tree,
    material,
    financialPeriod,
    materialPrice,
    partWaxPrice,
    treeWaxPrice,
  } = entities;

  // material.remelt is the calculator's input but the DB column is noRemelt.
  const remelt = material ? !material.noRemelt : undefined;

  return {
    partsPerTree: watch("partsPerTree"),
    weight: watch("weight"),
    scrapRate: watch("estimatedScrapRate"),
    treeMetalLossFactor: 0.2,
    tree: tree ? { weight: tree.weight } : undefined,
    material: material
      ? { remelt, price: materialPrice ?? null }
      : undefined,
    waxPartCost: partWaxPrice,
    waxTreeCost: treeWaxPrice,
    historical: financialPeriod
      ? {
          treeCost: financialPeriod.historicalTreeCost,
          materialCost: financialPeriod.historicalMaterialCost,
          factor: financialPeriod.historicalFactor,
        }
      : undefined,
    ...extras,
  };
};

// Returns the COGS value (calculation id 25) from current form + entity values.
export const getCogs = (watch, entities = {}, extras = {}) => {
  const values = getQuoteFormValues(watch, entities, extras);
  const results = resolveCalculations(values);
  return results[25] ?? 0;
};

export const formatValue = (value, type) => {
  if (value == null || isNaN(value)) return "—";

  switch (type) {
    case "currency":
      return `$${Number(value).toFixed(2)}`;
    case "lbs":
      return `${Number(value).toFixed(2)} lbs`;
    case "percentage":
      return `${Number(value * 100).toFixed(1)}%`;
    case "boolean":
      return value ? "Yes" : "No";
    default:
      return Number(value).toFixed(2);
  }
};
